import React, { useMemo, useState } from "react";
import {
  useAddInputBatchMutation,
  useDeleteInputBatchMutation,
  useGetInputBatchesQuery,
  useUpdateInputBatchMutation,
} from "../../features/inputBatches/inputBatchApi";
import { useGetRawMaterialsQuery } from "../../features/rawMaterials/rawMaterialApi";
import { useGetItemsQuery } from "../../features/items/itemApi";
import { usePlantSelection } from "../../features/plants/PlantSelectionContext";
import MasterCrudPage from "../masters/MasterCrudPage";
import { buildNameCodeIdLabel, getPlantLabel, renderPlantCell } from "../masters/masterMetadata";

const filterFields = [
  { name: "input_ready_batch_id", label: "Input Batch ID" },
  { name: "raw_material_id", label: "Raw Material", optionSource: "rawMaterials" },
  { name: "raw_material_code", label: "Raw Material Code" },
  { name: "item_id", label: "Finished Item", optionSource: "items" },
  { name: "qr_code", label: "QR Code" },
  { name: "is_processed", label: "Processed", type: "select", options: [
    { value: "false", label: "No" },
    { value: "true", label: "Yes" },
  ] },
];
const getQrValue = (row) => row?.qr_code || row?.input_ready_batch_id;
const columns = (metaData) => [
  { name: "Input Batch ID", selector: (row) => row.input_ready_batch_id || "-", sortable: true },
  { name: "Raw Material ID", selector: (row) => row.raw_material_id || "-", sortable: true },
  { name: "Raw Material Code", selector: (row) => row.raw_material_code || "-", sortable: true },
  { name: "Quantity", selector: (row) => row.quantity ?? "-", sortable: true },
  { name: "Finished Item", selector: (row) => row.item_code || row.item_id || "-", sortable: true },
  { name: "Processed", selector: (row) => row.is_processed ? "Yes" : "No", sortable: true },
  { name: "Plant", selector: (row) => getPlantLabel(row, metaData), cell: (row) => renderPlantCell(row, metaData), sortable: true, grow: 2 },
];
const searchFields = [
  "input_ready_batch_id", "raw_material_id", "raw_material_code", "item_id",
  "item_code", "qr_code", "plant_id", (row, metaData) => getPlantLabel(row, metaData),
];

const InputBatch = () => {
  const { selectedPlantId } = usePlantSelection();
  const [formPlantId, setFormPlantId] = useState("");
  const optionPlantId = formPlantId || selectedPlantId;
  const queryArgs = { page: 1, limit: 1000, plant_id: optionPlantId };
  const materialsQuery = useGetRawMaterialsQuery(queryArgs, { skip: !optionPlantId });
  const itemsQuery = useGetItemsQuery(queryArgs, { skip: !optionPlantId });
  const materials = materialsQuery.currentData?.data;
  const items = itemsQuery.currentData?.data;
  const fields = useMemo(() => [
    {
      name: "raw_material_id", label: "Raw Material", type: "select", required: true, fullWidth: true,
      options: (formData, editData) => {
        const options = (materials || []).map((material) => ({
          value: material.raw_material_id,
          label: buildNameCodeIdLabel({ name: material.raw_material_name, code: material.raw_material_code, id: material.raw_material_id }),
        }));
        if (editData?.raw_material_id && !options.some((option) => option.value === editData.raw_material_id)) {
          options.push({ value: editData.raw_material_id, label: `${editData.raw_material_id} (${editData.raw_material_code || "Unavailable"})` });
        }
        return options;
      },
      placeholder: materialsQuery.isFetching ? "Loading raw materials..." : "Select raw material",
      disabled: materialsQuery.isFetching || materialsQuery.isError,
    },
    { name: "quantity", label: "Quantity", type: "number", min: 1, step: 1, required: true, placeholder: "Enter quantity" },
    {
      name: "item_id", label: "Finished Item (optional)", type: "select",
      options: (formData, editData) => {
        const options = [{ value: "", label: "No finished item" }, ...(items || []).map((item) => ({
          value: item.item_id,
          label: buildNameCodeIdLabel({ name: item.item_name, code: item.item_code, id: item.item_id }),
        }))];
        if (editData?.item_id && !options.some((option) => option.value === editData.item_id)) {
          options.push({ value: editData.item_id, label: editData.item_code || editData.item_id });
        }
        return options;
      },
      placeholder: itemsQuery.isFetching ? "Loading items..." : "Select finished item",
      disabled: itemsQuery.isFetching || itemsQuery.isError,
    },
    { name: "qr_code", label: "QR Code", placeholder: "Enter QR code (optional)" },
    { name: "is_processed", label: "Processed", type: "checkbox" },
  ], [materials, items, materialsQuery.isFetching, materialsQuery.isError, itemsQuery.isFetching, itemsQuery.isError]);

  return (
    <MasterCrudPage
      entityName="Input Batch"
      pageTitle="Input Batches"
      idField="input_ready_batch_id"
      displayField="input_ready_batch_id"
      fields={fields}
      columns={columns}
      searchFields={searchFields}
      searchPlaceholder="Search input batches"
      useListQuery={useGetInputBatchesQuery}
      useCreateMutation={useAddInputBatchMutation}
      useUpdateMutation={useUpdateInputBatchMutation}
      useDeleteMutation={useDeleteInputBatchMutation}
      filterFields={filterFields}
      qrValue={getQrValue}
      onOpenForm={(row) => {
        const plantId = row?.plant_id || selectedPlantId;
        setFormPlantId(plantId);
        if (plantId === optionPlantId) {
          if (materialsQuery.isError) materialsQuery.refetch();
          if (itemsQuery.isError) itemsQuery.refetch();
        }
      }}
      formDisabled={materialsQuery.isFetching || materialsQuery.isError || itemsQuery.isFetching || itemsQuery.isError}
      extraFormContent={(materialsQuery.isError || itemsQuery.isError) ? (
        <p role="alert" className="text-sm text-red-600">Unable to load raw materials or items. Close the form and try again.</p>
      ) : null}
      transformPayload={(payload, editData) => ({
        ...payload,
        plant_id: editData?.plant_id || selectedPlantId,
        raw_material_code: materials?.find((material) => material.raw_material_id === payload.raw_material_id)?.raw_material_code
          ?? (editData?.raw_material_id === payload.raw_material_id ? editData.raw_material_code : null),
        item_id: payload.item_id || null,
        item_code: payload.item_id ? (items?.find((item) => item.item_id === payload.item_id)?.item_code
          ?? (editData?.item_id === payload.item_id ? editData.item_code : null)) : null,
        qr_code: payload.qr_code || null,
      })}
      noDataLabel="No input batches found"
    />
  );
};

export default InputBatch;
